import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { api, setAuthToken, getAuthToken, type ApiError } from '@/utils/api';
import type { Employee } from '@/types';

/**
 * Auth store.
 *
 * Flujo:
 *   1. El usuario toca "Ingresar con Google" → recibimos `id_token` del flow OAuth.
 *   2. POST /v1/auth/google { id_token } → backend valida contra Google + Workspace,
 *      revuelve { token: <jwt>, employee }.
 *   3. Guardamos JWT en localStorage y `employee` en memoria/store.
 *
 * `restore()` se llama en boot de App.vue para rehidratar la sesión.
 */

export const useAuthStore = defineStore('auth', () => {
  const employee = ref<Employee | null>(null);
  const loading = ref(false);
  const error = ref<string | null>(null);
  const hydrated = ref(false);

  const isAuthenticated = computed(() => employee.value !== null && !!getAuthToken());
  // Supervisor en el flujo nuevo = quien puede aprobar pedidos.
  // (Renato y Gastón tienen can_approve=true; Renato tiene can_pick=false.)
  const isSupervisor = computed(() => !!employee.value?.can_approve);

  async function restore(): Promise<void> {
    if (hydrated.value) return;
    const token = getAuthToken();
    if (!token) {
      hydrated.value = true;
      return;
    }
    try {
      const { data } = await api.get<{ employee: Employee }>('/v1/auth/me');
      employee.value = data.employee;
    } catch {
      // token inválido o expirado: limpiamos
      setAuthToken(null);
      employee.value = null;
    } finally {
      hydrated.value = true;
    }
  }

  async function loginWithGoogle(idToken: string): Promise<void> {
    loading.value = true;
    error.value = null;
    try {
      const { data } = await api.post<{ token: string; employee: Employee }>(
        '/v1/auth/google',
        { id_token: idToken },
      );
      setAuthToken(data.token);
      employee.value = data.employee;
    } catch (e) {
      const apiErr = e as ApiError;
      error.value = apiErr.message ?? 'Error al iniciar sesión';
      throw e;
    } finally {
      loading.value = false;
    }
  }

  // Modo prueba (VITE_DEV_LOGIN=1): login directo por email, sin Google.
  async function loginDev(email: string): Promise<void> {
    loading.value = true;
    error.value = null;
    try {
      const { data } = await api.post<{ token: string; employee: Employee }>(
        '/v1/auth/dev-login',
        { email },
      );
      setAuthToken(data.token);
      employee.value = data.employee;
    } catch (e) {
      const apiErr = e as ApiError;
      error.value = apiErr.message ?? 'Error al iniciar sesión';
      throw e;
    } finally {
      loading.value = false;
    }
  }

  function logout(): void {
    setAuthToken(null);
    employee.value = null;
  }

  return {
    employee,
    loading,
    error,
    hydrated,
    isAuthenticated,
    isSupervisor,
    restore,
    loginWithGoogle,
    loginDev,
    logout,
  };
});
